import React from 'react';
import { motion } from 'framer-motion';
import { Layers, ArrowRight, Users, Building2, Crown, Globe } from 'lucide-react';
export function NCJIPage() {
  return <div className="min-h-screen bg-surface-bg" data-id="element-1289" data-mp="1pywwfup125hy">
      {/* Hero Section */}
      <section className="pt-32 pb-20 lg:pt-40 lg:pb-28 px-4 sm:px-6 lg:px-8 max-w-5xl mx-auto" data-id="element-1290" data-mp="1q0zxd9rpxj1g">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6
      }} className="text-center" data-id="element-1291" data-mp="1v045ry6tjcqq">
          <div className="inline-flex items-center gap-2 mb-6" data-id="element-1292" data-mp="176uemg0ngj64">
            <span className="w-8 h-px bg-teal" data-id="element-1293" data-mp="1c7s65j7jupka"></span>
            <span className="text-xs font-semibold text-teal uppercase tracking-[0.2em]" data-id="element-1294" data-mp="1c5ffmhnq76vm">
              Neurocognitive Civic Judgment Index
            </span>
            <span className="w-8 h-px bg-teal" data-id="element-1295" data-mp="2nlm86w9j7wh0"></span>
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold text-ink-dark leading-tight mb-8" data-id="element-1296" data-mp="1b9xzymou8pqu">
            A Lifecycle Framework for Human Capability, Civic Development, and
            Leadership Readiness
          </h1>

          <p className="text-xl text-ink-muted leading-relaxed max-w-3xl mx-auto" data-id="element-1297" data-mp="127yi5s601hmu">
            The Neurocognitive Civic Judgment Index (NCJI) expands the Human
            Capability Platform into a broader developmental architecture that helps
            translate capability gaps into growth pathways, support systems,
            institutional readiness, and leadership qualification across the
            full spectrum of social responsibility.
          </p>
        </motion.div>
      </section>

      {/* Introduction */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto" data-id="element-1298" data-mp="ei46vf1bw14p">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-1299" data-mp="9kcharo5or3o">
          <p className="text-lg text-ink-muted leading-relaxed mb-6" data-id="element-1300" data-mp="3w3xni7s7qkw7">
            Where CJI provides the analytical foundation, NCJI expands that
            foundation into a full-spectrum system for human opportunity,
            lifecycle development, institutional readiness, civic resilience,
            and leadership qualification.
          </p>
          <p className="text-lg text-ink-muted leading-relaxed" data-id="element-1301" data-mp="inzm7qpxxsj4">
            It is designed not merely to identify limitations, but to convert
            them into pathways. Every meaningful weakness should be paired with
            a development route. Every barrier should be translated into a plan.
            Every plan should connect to some combination of self-directed
            action, institutional support, community reinforcement, or
            public-sector infrastructure.
          </p>
        </motion.div>
      </section>

      {/* From Assessment to Advancement */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white border-y border-line" data-id="element-1302" data-mp="2h634o311374e">
        <div className="max-w-5xl mx-auto" data-id="element-1303" data-mp="2vug0n0048pyw">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-1304" data-mp="2vt4oocwiobwp">
            <div className="flex items-center gap-4 mb-8" data-id="element-1305" data-mp="1y91y4drtsyyc">
              <div className="w-12 h-12 rounded-lg bg-navy flex items-center justify-center text-white" data-id="element-1306" data-mp="2d4q0ryhhxe04">
                <ArrowRight size={24} data-id="element-1307" data-mp="283ads0vrcxf3" />
              </div>
              <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-1308" data-mp="3oilmxr6tin0u">
                From Assessment to Advancement
              </h2>
            </div>
            <p className="text-lg text-ink-muted leading-relaxed mb-10" data-id="element-1309" data-mp="3jm4oa31zsq9i">
              NCJI operates on a simple progression model that allows the
              framework to identify capability gaps, clarify what they mean,
              recommend concrete interventions, connect individuals or
              institutions to support structures, track progress over time, and
              unlock wider opportunity as readiness improves.
            </p>

            {/* Process Flow */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4" data-id="element-1310" data-mp="2c1itij0sb1nj">
              {[{
              step: 'Assess',
              desc: 'Identify gaps'
            }, {
              step: 'Explain',
              desc: 'Clarify meaning'
            }, {
              step: 'Recommend',
              desc: 'Concrete steps'
            }, {
              step: 'Support',
              desc: 'Connect resources'
            }, {
              step: 'Reassess',
              desc: 'Track progress'
            }, {
              step: 'Advance',
              desc: 'Unlock opportunity'
            }].map((item, index) => <motion.div key={item.step} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.4,
              delay: index * 0.1
            }} className="text-center" data-id="element-1311" data-mp="2vvshu4j40hrd" data-mpi={index}>
                  <div className="w-12 h-12 rounded-full bg-teal/10 border-2 border-teal mx-auto mb-3 flex items-center justify-center" data-id="element-1312" data-mp="f8g8cet8qrux">
                    <span className="text-sm font-bold text-teal" data-id="element-1313" data-mp="24o0oc84gfvuh">
                      {index + 1}
                    </span>
                  </div>
                  <h3 className="font-heading font-bold text-ink-dark text-sm mb-1" data-id="element-1314" data-mp="29lsiz9whhw2d">
                    {item.step}
                  </h3>
                  <p className="text-xs text-ink-muted" data-id="element-1315" data-mp="1uwd16jexl0yt">{item.desc}</p>
                </motion.div>)}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Human Opportunity and Development */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-teal-pale" data-id="element-1316" data-mp="1qwvxnvj2edwa">
        <div className="max-w-4xl mx-auto" data-id="element-1317" data-mp="1bzcys0coakb4">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-1318" data-mp="1c0emb4avkz9q">
            <div className="flex items-center gap-4 mb-6" data-id="element-1319" data-mp="3ba39y3gzwdes">
              <div className="w-12 h-12 rounded-lg bg-teal flex items-center justify-center text-white" data-id="element-1320" data-mp="zpvq0i87gbch">
                <Users size={24} data-id="element-1321" data-mp="urc58l5cl5lp" />
              </div>
              <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-1322" data-mp="19endoe68g9hl">
                Human Opportunity and Development
              </h2>
            </div>
            <p className="text-lg text-ink-muted leading-relaxed" data-id="element-1323" data-mp="14mvwgu8yvg44">
              A defining feature of NCJI is its Human Opportunity and
              Development framework. The system is designed to increase
              self-awareness, guide individuals toward practical pathways for
              success, and strengthen communities through better alignment of
              talent, support, and resources. Rather than freezing people inside
              static categories, the framework is meant to identify practical
              growth opportunities and make them actionable.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Lifecycle Evolution */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto" data-id="element-1324" data-mp="33cckn1fh5qke">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-1325" data-mp="38b766b4ttt87">
          <div className="flex items-center gap-4 mb-6" data-id="element-1326" data-mp="2kfbevee3jmfe">
            <div className="w-12 h-12 rounded-lg bg-navy-light flex items-center justify-center text-white" data-id="element-1327" data-mp="2pcuscc2yzv6a">
              <Layers size={24} data-id="element-1328" data-mp="2ubo7sa1thw93" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-1329" data-mp="2uaw9grgu5kf4">
              A Platform That Evolves With Responsibility
            </h2>
          </div>
          <p className="text-lg text-ink-muted leading-relaxed mb-8" data-id="element-1330" data-mp="9mrumjl7u9ro">
            NCJI is structured as a full-spectrum lifecycle framework:
          </p>

          <div className="space-y-6" data-id="element-1331" data-mp="emwb49m4j4hw">
            {[{
            stage: 'Early Development',
            focus: 'Emotional regulation, cooperation, self-direction, and foundational reasoning'
          }, {
            stage: 'Education',
            focus: 'Logic, media literacy, planning, and consequential thinking'
          }, {
            stage: 'Adulthood',
            focus: 'Readiness and opportunity-alignment for workforce, entrepreneurship, civic participation, and public service'
          }, {
            stage: 'Institutional & Executive',
            focus: 'Leadership development, civil-service modernization, and high-responsibility decision environments'
          }].map((item, index) => <motion.div key={item.stage} initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: index * 0.1
          }} className="bg-white p-6 rounded-lg border border-line" data-id="element-1332" data-mp="2vtmjtyaqb3wp" data-mpi={index}>
                <h3 className="text-lg font-heading font-bold text-ink-dark mb-2" data-id="element-1333" data-mp="3nwglnmr41ol8">
                  {item.stage}
                </h3>
                <p className="text-ink-muted leading-relaxed" data-id="element-1334" data-mp="3iv13q3k8by72">{item.focus}</p>
              </motion.div>)}
          </div>
        </motion.div>
      </section>

      {/* Core Capability Domains */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-surface-muted border-y border-line" data-id="element-1335" data-mp="347y33o0m19fi">
        <div className="max-w-5xl mx-auto" data-id="element-1336" data-mp="306ckdm1ea0xe">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-1337" data-mp="305b4n49foq9c">
            <h2 className="text-3xl font-heading font-bold text-ink-dark mb-6 text-center" data-id="element-1338" data-mp="2g2f00ekegbz7">
              Core Capability Domains
            </h2>
            <p className="text-lg text-ink-muted leading-relaxed mb-10 text-center max-w-3xl mx-auto" data-id="element-1339" data-mp="38typuhbi4oa6">
              NCJI is built around foundational human capabilities that shape
              readiness across the lifecycle:
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-1340" data-mp="2ywtzs6ejc8o3">
              {[{
              name: 'Reasoning quality',
              link: '/reasoning-quality'
            }, {
              name: 'Consequential thinking',
              link: '/consequential-thinking'
            }, {
              name: 'Manipulation resistance',
              link: '/manipulation-resistance'
            }, {
              name: 'Emotional regulation',
              link: '/emotional-regulation'
            }, {
              name: 'Social cooperation',
              link: '/social-cooperation'
            }, {
              name: 'Self-direction and execution',
              link: '/self-direction'
            }, {
              name: 'Leadership responsibility',
              link: '/leadership-responsibility'
            }].map((domain, index) => <motion.a key={domain.name} href={domain.link} initial={{
              opacity: 0,
              scale: 0.95
            }} whileInView={{
              opacity: 1,
              scale: 1
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.4,
              delay: index * 0.05
            }} className="bg-white p-5 rounded-lg border border-line flex items-center gap-3 hover:border-navy hover:shadow-md transition-all group" data-id="element-1341" data-mp="2xfongz660q94" data-mpi={index}>
                  <div className="w-2 h-2 rounded-full bg-navy flex-shrink-0" data-id="element-1342" data-mp="k98l4o99lj9p"></div>
                  <span className="text-ink-dark font-medium group-hover:text-navy transition-colors" data-id="element-1343" data-mp="p679eq68yvzz">
                    {domain.name}
                  </span>
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" className="ml-auto opacity-0 group-hover:opacity-100 transition-opacity text-navy" data-id="element-1344" data-mp="z56a9mkrtncf">
                    <path d="M6 12L10 8L6 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-1345" data-mp="286prqc09a4c3" />
                  </svg>
                </motion.a>)}
            </div>

            <p className="text-lg text-ink-muted leading-relaxed mt-10 text-center" data-id="element-1346" data-mp="2r2zctj9krv5l">
              These domains create a common framework for understanding how
              individuals develop, how institutions strengthen, and how
              leadership can be evaluated more rigorously over time.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Built For */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 max-w-5xl mx-auto" data-id="element-1347" data-mp="3ar8eql0x7fia">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-1348" data-mp="3ap43fi0oze7z">
          <div className="flex items-center gap-4 mb-8 justify-center" data-id="element-1349" data-mp="3ffzr62p1u1xn">
            <div className="w-12 h-12 rounded-lg bg-green-soft flex items-center justify-center text-white" data-id="element-1350" data-mp="3ffrb61xmqgi8">
              <Building2 size={24} data-id="element-1351" data-mp="3kgx8ye65ctym" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-1352" data-mp="35p544w3zayb5">
              Built for Individuals, Institutions, and Nations
            </h2>
          </div>

          <p className="text-lg text-ink-muted leading-relaxed mb-10 text-center max-w-3xl mx-auto" data-id="element-1353" data-mp="2wprua2by07pw">
            NCJI is designed for use across multiple scales and environments:
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1354" data-mp="2mrltxp5sb9n5">
            {[{
            icon: <Users size={20} data-id="element-1355" data-mp="2rqyeg396o4mk" />,
            title: 'Individual Level',
            desc: 'Human development, self-awareness, and opportunity guidance',
            link: '/individual-level'
          }, {
            icon: <Building2 size={20} data-id="element-1356" data-mp="23tiip6s0erao" />,
            title: 'Organizational Level',
            desc: 'Readiness and capability-building inside institutions',
            link: '/organizational-level'
          }, {
            icon: <Globe size={20} data-id="element-1357" data-mp="2rl7jlbiwco38" />,
            title: 'Municipal Scale',
            desc: 'Local civic resilience and community strengthening',
            link: '/municipal-scale'
          }, {
            icon: <Crown size={20} data-id="element-1358" data-mp="2hl6uta29dfrn" />,
            title: 'National Scale',
            desc: 'Strategic planning for civic resilience, institutional strengthening, and leadership qualification',
            link: '/national-scale'
          }].map((item, index) => <motion.a key={item.title} href={item.link} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: index * 0.1
          }} className="bg-white p-6 rounded-lg border border-line hover:border-navy hover:shadow-md transition-all group" data-id="element-1359" data-mp="117grw6mvkxjl" data-mpi={index}>
                <div className="flex items-center gap-3 mb-3" data-id="element-1360" data-mp="4hv1qaxojupr">
                  <div className="w-10 h-10 rounded-md bg-surface-muted flex items-center justify-center text-navy-light group-hover:bg-navy group-hover:text-white transition-colors" data-id="element-1361" data-mp="9g6f2vn6oncq">
                    {item.icon}
                  </div>
                  <h3 className="text-lg font-heading font-bold text-ink-dark group-hover:text-navy transition-colors" data-id="element-1362" data-mp="2b8kvfqa2xjr1">
                    {item.title}
                  </h3>
                </div>
                <p className="text-ink-muted leading-relaxed mb-3" data-id="element-1363" data-mp="lv4ea0hwqokw">
                  {item.desc}
                </p>
                <div className="flex items-center gap-1 text-navy text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-1364" data-mp="vn0qsgkqqh8n">
                  Learn More
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-1365" data-mp="qqab3uculnnr">
                    <path d="M6 12L10 8L6 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-1366" data-mp="cwixa9s33hm8" />
                  </svg>
                </div>
              </motion.a>)}
          </div>
        </motion.div>
      </section>

      {/* NCJI Within SyNeural */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto" data-id="element-1367" data-mp="1qvcd54cxrklv">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="bg-navy-deep text-white p-12 rounded-2xl" data-id="element-1368" data-mp="1vw04xs8zo5h5">
          <h2 className="text-3xl font-heading font-bold mb-6" data-id="element-1369" data-mp="29p7y3ocpx90y">
            NCJI Within SyNeural's Human Capability Platform
          </h2>
          <p className="text-lg leading-relaxed opacity-90" data-id="element-1370" data-mp="20vcz6y26wetr">
            NCJI is the broader architecture built on top of CJI. Where CJI
            provides the analytical and diagnostic foundation, NCJI expands the
            system into lifecycle development, opportunity guidance,
            institutional readiness, and executive-level leadership evaluation.
            Together, CJI and NCJI form a unified platform for measuring,
            developing, and scaling judgment across the full human and
            institutional spectrum.
          </p>
        </motion.div>
      </section>
    </div>;
}